<script setup>
/**
 * The lines of a "Bullet list" question: one box per line, as many as needed
 * (none is a fine answer). Enter in a line starts the next one; the blank
 * ones are dropped when the answer is saved.
 *
 * v-model: string[]
 */
import {nextTick, ref} from 'vue'

const props = defineProps({
  value: {type: Array, default: () => []},
  disabled: {type: Boolean, default: false},
  lead: {type: String, default: ''},
})
const emit = defineEmits(['input', 'submit'])

const boxes = ref([])

function set(i, v) {
  const lines = [...props.value]
  lines[i] = v
  emit('input', lines)
}

async function add(after = props.value.length - 1) {
  const lines = [...props.value]
  lines.splice(after + 1, 0, '')
  emit('input', lines)
  await nextTick()
  const box = boxes.value[after + 1]
  if (box) box.focus()
}

function remove(i) {
  emit('input', props.value.filter((_, k) => k !== i))
}

// Enter: next line; Ctrl+Enter: done with the question
function onKey(e, i) {
  if (e.key !== 'Enter') return
  e.preventDefault()
  if (e.ctrlKey || e.metaKey) emit('submit')
  else add(i)
}
</script>

<template>
  <div class="mt-2">
    <div class="small text-muted mb-1" v-if="lead">In the listing: <em>{{ lead }}</em>, then one line each below.</div>
    <div v-for="(line, i) in value" :key="i" class="d-flex align-items-center mb-1">
      <span class="text-muted mr-2">&bull;</span>
      <input ref="boxes" class="form-control form-control-sm" :value="line" :disabled="disabled" maxlength="300"
             placeholder="One item per line" @input="set(i, $event.target.value)" @keydown="onKey($event, i)">
      <b-btn size="sm" variant="link" class="text-muted px-2" title="Remove this line" :disabled="disabled" @click="remove(i)">
        <font-awesome-icon icon="fa-solid fa-xmark"/>
      </b-btn>
    </div>
    <b-btn size="sm" variant="outline-secondary" :disabled="disabled" @click="add()">
      <font-awesome-icon icon="fa-solid fa-plus" class="mr-1"/>Add a line
    </b-btn>
    <div class="small text-muted mt-1" v-if="!value.length">No lines is fine - nothing goes in the listing for this question.</div>
  </div>
</template>
